export interface UserProfile {
  client_id: string;
  user_id: string | null;
  full_name: string | null;
  job_title: string | null;
  company_name: string | null;
  phone_number: string | null;
  profile_picture_url: string | null;
  company_logo_url: string | null;
  company_website: string | null;
  country: string | null;
  company_industry: string | null;
  email_address: string | null;
  time_zone: string | null;
  language: string | null;
}

export type CompanyMaterialSource = 'upload' | 'link';
export type CompanyMaterialCategory =
  | 'company_profile'
  | 'video_explainer'
  | 'case_study'
  | 'lead_magnet'
  | 'guide'
  | 'call_recording'
  | 'work_examples'
  | 'other';

export const MATERIAL_CATEGORY_LABELS: Record<CompanyMaterialCategory, string> = {
  company_profile: 'Company Profile',
  video_explainer: 'Product Video Explainer',
  case_study: 'Case Study',
  lead_magnet: 'Lead Magnet',
  guide: 'Guide / Playbook',
  call_recording: 'Call Recording',
  work_examples: 'Work Examples',
  other: 'Other',
};

export interface CompanyMaterial {
  id: string;
  name: string;
  url: string;
  size: number;
  type: string;
  uploaded_at: string;
  is_company_profile?: boolean;
  source: CompanyMaterialSource;
  category?: CompanyMaterialCategory;
  description?: string;
}

export interface ScriptSection {
  id: string;
  title: string;
  content: string;
  placeholder: string;
}

export interface ObjectionItem {
  id: string;
  title: string;
  response: string;
  category?: 'timing' | 'budget' | 'authority' | 'need' | 'competition' | 'other';
  order: number;
}

export interface CallScript1 {
  greetings: string;
  introduction: string;
  why_we_are_calling: string;
  elaboration_credibility: string;
  engagement: string;
  qualification: string;
  next_steps: string;
  booking: string;
  closing: string;
}

export interface CallScript2 {
  greetings: string;
  introduction: string;
  why_we_are_calling_again: string;
  engagement: string;
  qualification_if_not_done: string;
  next_steps: string;
  booking: string;
  closing: string;
}

export interface CallAgentSettings {
  script_1: CallScript1;
  script_2: CallScript2;
  objection_handling: ObjectionItem[];
  vapi_agent_id?: string;
  agent_name: string;
  gender: string;
  nationality: string[];
  location: string;
  languages?: string[];
  tone: string;
  tone_instructions?: string;
  role: string;
  objective: string;
  phone_number: string;
  phone_number_id?: string;
  calling_number?: string;
  max_calls_per_lead: number;
  do_rules: { id: string; text: string }[];
  dont_rules: { id: string; text: string }[];
}

export type WaTemplateApprovalStatus = 'draft' | 'active' | 'in_review' | 'rejected';

export interface EmailTemplateDraft {
  id: string;
  campaign_id: string;
  template_key: string;
  subject_text: string;
  body_text: string;
  source: string;
  status: 'draft' | 'approved';
  submitted_at: string;
  approved_at: string | null;
}

export interface LinkedInTemplateDraft {
  id: string;
  campaign_id: string;
  template_key: string;
  step_name: string;
  message_text: string;
  source: string;
  status: 'draft' | 'approved';
  submitted_at: string;
  approved_at: string | null;
}

export interface CallScriptDraft {
  id: string;
  campaign_id: string;
  script_data: {
    script_1?: Partial<CallScript1>;
    script_2?: Partial<CallScript2>;
    objection_handling?: Array<{
      id: string;
      title: string;
      response: string;
      category: string;
      order: number;
    }>;
  };
  source: string;
  status: 'draft' | 'approved';
  submitted_at: string;
  approved_at: string | null;
}

export interface WaTemplateSubmission {
  id: string;
  campaign_id: string | null;
  template_key: WhatsAppTemplateKey;
  submitted_text: string;
  submitted_button_text: string;
  submitted_button_url: string;
  status: 'pending_review' | 'approved' | 'rejected';
  rejection_reason: string | null;
  submitted_at: string;
  reviewed_at: string | null;
}

export interface WhatsAppTemplateMessage {
  text: string;
  button_text?: string;
  button_url?: string;
  approval_status?: WaTemplateApprovalStatus;
  pending_submission?: WaTemplateSubmission | null;
}

export type WhatsAppTemplateKey =
  | 'wa_outreach_first'
  | 'wa_outreach_fu_cold_1'
  | 'wa_outreach_fu_cold_2'
  | 'wa_outreach_fu_cold_3'
  | 'wa_outreach_fu_warm_1'
  | 'wa_outreach_fu_warm_2'
  | 'wa_outreach_fu_warm_3'
  | 'wa_inbound_first'
  | 'wa_inbound_fu_1'
  | 'wa_inbound_fu_2'
  | 'wa_inbound_lm_first'
  | 'wa_inbound_lm_fu_1'
  | 'wa_inbound_lm_fu_2'
  | 'wa_other_info_request'
  | 'wa_other_meeting_confirmation'
  | 'wa_other_meeting_remind_24'
  | 'wa_other_meeting_remind_4'
  | 'wa_other_meeting_remind_30min'
  | 'wa_other_rescheduled_meeting';

export type WhatsAppTemplates = Record<WhatsAppTemplateKey, WhatsAppTemplateMessage>;

export interface WhatsAppTemplateMeta {
  key: WhatsAppTemplateKey;
  label: string;
  number: number;
  category: 'cold_outreach' | 'inbound' | 'other';
  isSequenceStep: boolean;
  placeholder: string;
}

export const WA_TEMPLATE_DEFINITIONS: WhatsAppTemplateMeta[] = [
  { key: 'wa_outreach_first', label: 'First Outreach Message', number: 1, category: 'cold_outreach', isSequenceStep: true, placeholder: 'Hi {First_Name}! I\'m {Agent_Name} from {Company}...' },
  { key: 'wa_outreach_fu_cold_1', label: 'Follow Up 1 - No Answer', number: 2, category: 'cold_outreach', isSequenceStep: true, placeholder: 'Hi {First_Name}, just following up on my previous message...' },
  { key: 'wa_outreach_fu_cold_2', label: 'Follow Up 2 - No Answer', number: 3, category: 'cold_outreach', isSequenceStep: true, placeholder: 'Hey {First_Name}, reaching out one more time...' },
  { key: 'wa_outreach_fu_cold_3', label: 'Follow Up 3 - No Answer', number: 4, category: 'cold_outreach', isSequenceStep: true, placeholder: 'Hi {First_Name}, I understand you\'re busy...' },
  { key: 'wa_outreach_fu_warm_1', label: 'Follow Up 1 - Engaged', number: 5, category: 'cold_outreach', isSequenceStep: true, placeholder: 'Hi {First_Name}, great chatting with you! I wanted to follow up on what we discussed...' },
  { key: 'wa_outreach_fu_warm_2', label: 'Follow Up 2 - Engaged', number: 6, category: 'cold_outreach', isSequenceStep: true, placeholder: 'Hi {First_Name}, just circling back on our conversation...' },
  { key: 'wa_outreach_fu_warm_3', label: 'Follow Up 3 - Engaged', number: 7, category: 'cold_outreach', isSequenceStep: true, placeholder: 'Hi {First_Name}, I hope you\'ve had a chance to think about what we discussed...' },
  { key: 'wa_inbound_first', label: 'Inbound Inquiry - First Response', number: 8, category: 'inbound', isSequenceStep: true, placeholder: 'Hi {First_Name}! Thanks for reaching out to {Company}. I\'d love to learn more about what you\'re looking for...' },
  { key: 'wa_inbound_fu_1', label: 'Inbound Inquiry - Follow Up 1', number: 9, category: 'inbound', isSequenceStep: true, placeholder: 'Hi {First_Name}, just following up on your inquiry. I wanted to make sure you got everything you needed...' },
  { key: 'wa_inbound_fu_2', label: 'Inbound Inquiry - Follow Up 2', number: 10, category: 'inbound', isSequenceStep: true, placeholder: 'Hi {First_Name}, circling back on our conversation. Would love to help you take the next step...' },
  { key: 'wa_inbound_lm_first', label: 'Inbound Lead Magnet - First Response', number: 11, category: 'inbound', isSequenceStep: true, placeholder: 'Hi {First_Name}! Thanks for downloading our resource. I\'d love to help you get the most out of it...' },
  { key: 'wa_inbound_lm_fu_1', label: 'Inbound Lead Magnet - Follow Up 1', number: 12, category: 'inbound', isSequenceStep: true, placeholder: 'Hi {First_Name}, did you get a chance to look through what you downloaded? Happy to answer any questions...' },
  { key: 'wa_inbound_lm_fu_2', label: 'Inbound Lead Magnet - Follow Up 2', number: 13, category: 'inbound', isSequenceStep: true, placeholder: 'Hi {First_Name}, wanted to share how others have used this to get results. Would love to show you...' },
  { key: 'wa_other_info_request', label: 'Prospect Asked for More Info', number: 14, category: 'other', isSequenceStep: false, placeholder: 'Hi {First_Name}, thanks for your interest! Here\'s what you asked about...' },
  { key: 'wa_other_meeting_confirmation', label: 'Meeting Booked Confirmation', number: 15, category: 'other', isSequenceStep: false, placeholder: 'Hi {First_Name}, your meeting with {Agent_Name} is confirmed...' },
  { key: 'wa_other_meeting_remind_24', label: 'Meeting Reminder - 24 Hours Before', number: 16, category: 'other', isSequenceStep: false, placeholder: 'Hi {First_Name}, just a friendly reminder about our meeting tomorrow...' },
  { key: 'wa_other_meeting_remind_4', label: 'Meeting Reminder - 4 Hours Before', number: 17, category: 'other', isSequenceStep: false, placeholder: 'Hi {First_Name}, looking forward to our call in a few hours...' },
  { key: 'wa_other_meeting_remind_30min', label: 'Meeting Reminder - 30 Min Before', number: 18, category: 'other', isSequenceStep: false, placeholder: 'Hi {First_Name}, we\'re meeting in 30 minutes...' },
  { key: 'wa_other_rescheduled_meeting', label: 'Rescheduled Meeting', number: 19, category: 'other', isSequenceStep: false, placeholder: 'Hi {First_Name}, your meeting has been rescheduled...' },
];

export interface WhatsAppAgentSettings {
  mode: 'template' | 'ai';
  templates?: WhatsAppTemplates;
  agent_name: string;
  gender: string;
  nationality: string[];
  location: string;
  languages?: string[];
  tone: string;
  tone_instructions?: string;
  role: string;
  objective: string;
  phone_number: string;
  whatsapp_number?: string;
  whatsapp_account_id?: string;
  voice_notes_allowed?: boolean;
  voice_note_example?: string;
  video_messages_allowed?: boolean;
  max_follow_ups: number;
  delay_hours: number;
  do_rules: { id: string; text: string }[];
  dont_rules: { id: string; text: string }[];
}

export type EmailTemplateKey =
  | 'email_outreach_pitch'
  | 'email_outreach_fu_1'
  | 'email_outreach_fu_2'
  | 'email_outreach_fu_3'
  | 'email_outreach_fu_4'
  | 'email_inbound_first'
  | 'email_inbound_fu_1'
  | 'email_inbound_fu_2'
  | 'email_inbound_lm_first'
  | 'email_inbound_lm_fu_1'
  | 'email_inbound_lm_fu_2'
  | 'email_other_info_request'
  | 'email_other_meeting_confirmation'
  | 'email_other_meeting_remind_24'
  | 'email_other_meeting_remind_4'
  | 'email_other_meeting_remind_30min'
  | 'email_other_rescheduled_meeting';

export interface EmailTemplateMessage {
  subject: string;
  body: string;
}

export type EmailTemplates = Record<EmailTemplateKey, EmailTemplateMessage>;

export interface EmailTemplateMeta {
  key: EmailTemplateKey;
  label: string;
  number: number;
  category: 'cold_outreach' | 'inbound' | 'other';
  isSequenceStep: boolean;
  subjectPlaceholder: string;
  bodyPlaceholder: string;
}

export const EMAIL_TEMPLATE_DEFINITIONS: EmailTemplateMeta[] = [
  { key: 'email_outreach_pitch', label: 'Outreach - Pitch Email', number: 1, category: 'cold_outreach', isSequenceStep: true, subjectPlaceholder: 'Quick question about {Company_Name}\'s growth', bodyPlaceholder: 'Hi {First_Name},\n\nI came across {Company_Name} and noticed you\'re in the {Industry} space...' },
  { key: 'email_outreach_fu_1', label: 'Outreach - Follow Up 1', number: 2, category: 'cold_outreach', isSequenceStep: true, subjectPlaceholder: 'Following up - {Company_Name} + {Company} partnership', bodyPlaceholder: 'Hi {First_Name},\n\nI wanted to follow up on my previous email...' },
  { key: 'email_outreach_fu_2', label: 'Outreach - Follow Up 2', number: 3, category: 'cold_outreach', isSequenceStep: true, subjectPlaceholder: 'Thought you\'d find this interesting - {Company_Name}', bodyPlaceholder: 'Hi {First_Name},\n\nI hope this email finds you well...' },
  { key: 'email_outreach_fu_3', label: 'Outreach - Follow Up 3', number: 4, category: 'cold_outreach', isSequenceStep: true, subjectPlaceholder: 'Last note about {Company_Name}\'s sales growth', bodyPlaceholder: 'Hi {First_Name},\n\nI know you\'re busy, so I\'ll keep this brief...' },
  { key: 'email_outreach_fu_4', label: 'Outreach - Follow Up 4', number: 5, category: 'cold_outreach', isSequenceStep: true, subjectPlaceholder: 'Closing the loop - {Company_Name}', bodyPlaceholder: 'Hi {First_Name},\n\nI\'ve reached out a few times now...' },
  { key: 'email_inbound_first', label: 'Inbound Inquiry - First Response', number: 6, category: 'inbound', isSequenceStep: true, subjectPlaceholder: 'Thanks for reaching out - {Company_Name} + {Company}', bodyPlaceholder: 'Hi {First_Name},\n\nThank you for your interest in {Company}! I\'d love to learn more about what {Company_Name} is looking for...' },
  { key: 'email_inbound_fu_1', label: 'Inbound Inquiry - Follow Up 1', number: 7, category: 'inbound', isSequenceStep: true, subjectPlaceholder: 'Following up on your inquiry - {Company_Name}', bodyPlaceholder: 'Hi {First_Name},\n\nJust following up on your recent inquiry to make sure you have everything you need...' },
  { key: 'email_inbound_fu_2', label: 'Inbound Inquiry - Follow Up 2', number: 8, category: 'inbound', isSequenceStep: true, subjectPlaceholder: 'Still interested? - {Company_Name} + {Company}', bodyPlaceholder: 'Hi {First_Name},\n\nI wanted to circle back on your inquiry. I\'d love to help you take the next step...' },
  { key: 'email_inbound_lm_first', label: 'Inbound Lead Magnet - First Response', number: 9, category: 'inbound', isSequenceStep: true, subjectPlaceholder: 'Here\'s your download - {Company}', bodyPlaceholder: 'Hi {First_Name},\n\nThanks for downloading our resource! Here\'s everything you need to get started...' },
  { key: 'email_inbound_lm_fu_1', label: 'Inbound Lead Magnet - Follow Up 1', number: 10, category: 'inbound', isSequenceStep: true, subjectPlaceholder: 'Did you get a chance to review it? - {Company}', bodyPlaceholder: 'Hi {First_Name},\n\nJust checking in to see if you had a chance to look through the resource I sent over...' },
  { key: 'email_inbound_lm_fu_2', label: 'Inbound Lead Magnet - Follow Up 2', number: 11, category: 'inbound', isSequenceStep: true, subjectPlaceholder: 'Want to take the next step? - {Company}', bodyPlaceholder: 'Hi {First_Name},\n\nI hope the resource was helpful! If you\'d like, I\'d be happy to show you how we can help {Company_Name} further...' },
  { key: 'email_other_info_request', label: 'Prospect Asked For More Info', number: 12, category: 'other', isSequenceStep: false, subjectPlaceholder: 'As requested - more info about {Company}', bodyPlaceholder: 'Hi {First_Name},\n\nThanks for your interest! As requested, here\'s more detail...' },
  { key: 'email_other_meeting_confirmation', label: 'Meeting Booked Confirmation', number: 13, category: 'other', isSequenceStep: false, subjectPlaceholder: 'Meeting confirmed - {Company_Name} + {Company}', bodyPlaceholder: 'Hi {First_Name},\n\nYour meeting with {Agent_Name} is confirmed...' },
  { key: 'email_other_meeting_remind_24', label: 'Meeting Reminder - 24 Hours Before', number: 14, category: 'other', isSequenceStep: false, subjectPlaceholder: 'Reminder: Our meeting tomorrow', bodyPlaceholder: 'Hi {First_Name},\n\nJust a friendly reminder about our meeting tomorrow...' },
  { key: 'email_other_meeting_remind_4', label: 'Meeting Reminder - 4 Hours Before', number: 15, category: 'other', isSequenceStep: false, subjectPlaceholder: 'See you in a few hours!', bodyPlaceholder: 'Hi {First_Name},\n\nLooking forward to our call in a few hours...' },
  { key: 'email_other_meeting_remind_30min', label: 'Meeting Reminder - 30 Min Before', number: 16, category: 'other', isSequenceStep: false, subjectPlaceholder: 'Starting in 30 minutes', bodyPlaceholder: 'Hi {First_Name},\n\nWe\'re meeting in 30 minutes...' },
  { key: 'email_other_rescheduled_meeting', label: 'Rescheduled Meeting', number: 17, category: 'other', isSequenceStep: false, subjectPlaceholder: 'Meeting rescheduled - new time confirmed', bodyPlaceholder: 'Hi {First_Name},\n\nYour meeting has been rescheduled...' },
];

export interface EmailAgentSettings {
  mode: 'preset' | 'ai';
  templates?: EmailTemplates;
  domain?: string;
  agent_name?: string;
  gender: string;
  nationality: string[];
  location: string;
  languages?: string[];
  role: string;
  objective: string;
  from_email: string;
  tone: string;
  tone_instructions?: string;
  email_layout: string;
  signature: string;
  max_emails: number;
  delay_days: number;
  do_rules: { id: string; text: string }[];
  dont_rules: { id: string; text: string }[];
  email_connection_id?: string;
}

export type LinkedInTemplateKey =
  | 'li_outreach_connection'
  | 'li_outreach_intro'
  | 'li_outreach_pitch'
  | 'li_outreach_fu_1'
  | 'li_outreach_fu_2'
  | 'li_outreach_fu_3';

export interface LinkedInTemplateMessage {
  text: string;
}

export type LinkedInTemplates = Record<LinkedInTemplateKey, LinkedInTemplateMessage>;

export interface LinkedInTemplateMeta {
  key: LinkedInTemplateKey;
  label: string;
  number: number;
  category: 'outreach';
  isSequenceStep: boolean;
  placeholder: string;
}

export const LI_TEMPLATE_DEFINITIONS: LinkedInTemplateMeta[] = [
  { key: 'li_outreach_connection', label: 'Connection Request Message', number: 1, category: 'outreach', isSequenceStep: true, placeholder: 'Hi {First_Name}, I noticed we both work in {Industry}. I\'d love to connect and exchange insights...' },
  { key: 'li_outreach_intro', label: 'Intro / Welcome Message (After Connection)', number: 2, category: 'outreach', isSequenceStep: true, placeholder: 'Thanks for connecting, {First_Name}! I work with companies like {Company_Name} to...' },
  { key: 'li_outreach_pitch', label: 'Pitch', number: 3, category: 'outreach', isSequenceStep: true, placeholder: 'Hi {First_Name}, I wanted to share how we\'ve helped companies in {Industry} achieve...' },
  { key: 'li_outreach_fu_1', label: 'Follow-up Message 1', number: 4, category: 'outreach', isSequenceStep: true, placeholder: 'Hi {First_Name}, I wanted to follow up on my previous message...' },
  { key: 'li_outreach_fu_2', label: 'Follow-up Message 2', number: 5, category: 'outreach', isSequenceStep: true, placeholder: 'Hey {First_Name}, reaching out one more time regarding...' },
  { key: 'li_outreach_fu_3', label: 'Follow-up Message 3', number: 6, category: 'outreach', isSequenceStep: true, placeholder: 'Hi {First_Name}, I don\'t want to keep bothering you, but I genuinely think...' },
];

export interface LinkedInAgentSettings {
  mode: 'template' | 'ai';
  templates?: LinkedInTemplates;
  agent_persona: string;
  languages?: string[];
  tone: string;
  tone_instructions?: string;
  role: string;
  objective: string;
  linkedin_account_id?: string;
  location?: string;
  voice_notes_allowed?: boolean;
  voice_note_example?: string;
  max_messages: number;
  delay_days: number;
  do_rules: { id: string; text: string }[];
  dont_rules: { id: string; text: string }[];
}

export interface CampaignStepData {
  campaign_step_id: string;
  campaign_id: string | null;
  campaign_type: string | null;
  task_type: string | null;
  step_order: number | null;
  delay_minutes: number | null;
  action_mode: string | null;
  json: Record<string, any> | null;
  is_active: boolean | null;
}

export interface LinkedInConnection {
  id: string;
  client_id: string;
  user_id: string;
  linkedin_profile_url: string | null;
  linkedin_profile_name: string | null;
  is_active: boolean | null;
  connected_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  unipile_account_id: string;
  account_type: string | null;
  daily_invite_limit: number | null;
  daily_message_limit: number | null;
  daily_profile_lookup_limit: number | null;
  last_action_at: string | null;
  unipile_account_dsn: string | null;
  country: string | null;
  voice_notes_allowed: boolean | null;
  voice_note_example: string | null;
  elevenlabs_voice_id: string | null;
}

export interface AgentScriptGenerationRequest {
  agent_type: 'call' | 'whatsapp' | 'email' | 'linkedin';
  section: string;
  script_number?: 1 | 2;
  context?: string;
  company_name?: string;
  industry?: string;
  tone?: string;
  length?: string;
  user_id?: string;
  client_id?: string;
  current_content?: string;
}

export interface AgentScriptGenerationResponse {
  generated_script: string;
  suggestions?: string[];
}

export interface TargetAudience {
  countries: string[];
  industries: string[];
  company_sizes: string[];
  decision_maker_titles: string[];
}

export interface AboutCompany {
  products_services: string;
  usp_statement: string;
  target_audience: TargetAudience;
  competitors: { id: string; text: string }[];
  pain_points: { id: string; text: string }[];
  solutions: { id: string; text: string }[];
  desires: { id: string; text: string }[];
  benefits: { id: string; text: string }[];
  prospect_questions: { id: string; text: string }[];
}

export interface PitchBookItem {
  id: string;
  text: string;
  is_ai_generated: boolean;
}

export interface PitchBookObjection {
  id: string;
  title: string;
  response: string;
  category: 'timing' | 'budget' | 'authority' | 'need' | 'competition' | 'other';
  is_ai_generated: boolean;
}

export interface PitchBook {
  hooks: PitchBookItem[];
  elaborations: PitchBookItem[];
  engaging_questions: PitchBookItem[];
  qualifying_questions: PitchBookItem[];
  objections: PitchBookObjection[];
}

export type ResearchStatus = 'idle' | 'scraping_website' | 'researching' | 'generating_insights' | 'completed' | 'failed';

export interface OnboardingInput {
  website_url: string;
  linkedin_url: string;
  industry: string;
  country: string;
  products_services: string;
}

export interface SalesMaterials {
  id?: string;
  client_id: string;
  user_id: string;
  about_company: AboutCompany;
  pitch_book: PitchBook;
  generation_status: 'not_generated' | 'generating' | 'generated';
  research_status: ResearchStatus;
  research_error: string | null;
  onboarding_input: OnboardingInput | null;
  created_at?: string;
  updated_at?: string;
}

export const EMPTY_ABOUT_COMPANY: AboutCompany = {
  products_services: '',
  usp_statement: '',
  target_audience: {
    countries: [],
    industries: [],
    company_sizes: [],
    decision_maker_titles: [],
  },
  competitors: [],
  pain_points: [],
  solutions: [],
  desires: [],
  benefits: [],
  prospect_questions: [],
};

export const EMPTY_PITCH_BOOK: PitchBook = {
  hooks: [],
  elaborations: [],
  engaging_questions: [],
  qualifying_questions: [],
  objections: [],
};
